import * as React from 'react';

import type { CourseFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/SpecializationEntitiesFragments';

export type CourseFilterableCourse = Pick<CourseFragment, 'id' | 'slug' | 'name'>;

const getCourseFilterOptions = (
  courses?: Array<Pick<CourseFragment, 'slug' | 'name'>> | null
): Array<Pick<CourseFragment, 'slug' | 'name'>> =>
  Array.from(new Map((courses ?? []).map((course) => [course.slug, course])).values());

type Options = {
  // Both the standalone reviews page and the XDP reviews module default to the first child
  // course (there's nothing sensible to show otherwise), so this defaults to true. The XDP
  // module opts out (passes false) for its reviews-experiment control group, which renders
  // nothing and shouldn't kick off a real reviews fetch for a course it'll never show.
  defaultToFirstCourse?: boolean;
};

/**
 * Shared course-filter-dropdown state for specialization/professional-certificate reviews
 * surfaces (the XDP reviews module and the standalone reviews page), deduped by slug.
 *
 * The default is derived on every render rather than baked into useState's initial value: on the
 * standalone reviews page, `courses` is still empty on the first render (before the page-data
 * query resolves), so a lazy useState initializer would capture `undefined` forever and never
 * pick up the first course once data arrives.
 */
export const useSpecializationCourseFilter = <T extends CourseFilterableCourse>(
  courses?: Array<T> | null,
  { defaultToFirstCourse = true }: Options = {}
) => {
  const courseFilterOptions = getCourseFilterOptions(courses);
  // `null` means "the user hasn't touched the filter yet" -- distinct from `undefined`, which
  // means "explicitly cleared back to unselected". A plain `??` can't tell those apart (an
  // explicit `undefined` would just fall back to the default again), so the two are tracked as
  // separate values here.
  const [selectedCourseSlugOverride, setSelectedCourseSlug] = React.useState<string | undefined | null>(null);
  const defaultSlug = defaultToFirstCourse ? courseFilterOptions[0]?.slug : undefined;
  const selectedCourseSlug = selectedCourseSlugOverride === null ? defaultSlug : selectedCourseSlugOverride;
  const selectedCourseId = courses?.find((course) => course.slug === selectedCourseSlug)?.id;

  return { courseFilterOptions, selectedCourseSlug, setSelectedCourseSlug, selectedCourseId };
};
